import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  FileText, ArrowLeft, ArrowRight, Plus, Trash2, Save, Download,
  User, Briefcase, GraduationCap, Wrench, Crown, Shield, Eye,
  Loader2, CheckCircle, LogIn
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Initial empty resume data
const EMPTY_RESUME = {
  full_name: '',
  professional_title: '',
  email: '',
  phone: '',
  location: '',
  linkedin: '',
  summary: '',
  skills_list: [],
  experience_list: [],
  education_list: [],
  cert_list: []
};

export const CreateResumePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // State
  const [step, setStep] = useState(1); // 1: Template, 2: Form+Preview
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [resumeData, setResumeData] = useState(EMPTY_RESUME);
  const [previewHtml, setPreviewHtml] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedResumeId, setSavedResumeId] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showPaywallModal, setShowPaywallModal] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [isPremiumUser, setIsPremiumUser] = useState(false);
  
  // Form section step: 1=Personal, 2=Experience, 3=Education, 4=Skills
  const [formSection, setFormSection] = useState(1);
  
  // Skills input
  const [newSkill, setNewSkill] = useState('');
  
  // Validation state
  const [validationErrors, setValidationErrors] = useState({});

  // Validation functions
  const validateEmail = (email) => {
    if (!email || !email.trim()) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return 'Please enter a valid email address';
    return null;
  };

  const validatePhone = (phone) => {
    if (!phone || !phone.trim()) return 'Phone number is required';
    // Allow + for country code, then only digits and spaces/dashes
    const cleanedPhone = phone.replace(/[\s\-()]/g, '');
    const phoneRegex = /^\+?[0-9]{10,15}$/;
    if (!phoneRegex.test(cleanedPhone)) return 'Please enter a valid phone number with country code (e.g., +91 98765 43210)';
    return null;
  };

  const validateName = (name) => {
    if (!name || !name.trim()) return 'Full name is required';
    if (name.trim().length < 2) return 'Name must be at least 2 characters';
    return null;
  };

  const validateForm = () => {
    const errors = {};
    
    const nameError = validateName(resumeData.full_name);
    if (nameError) errors.full_name = nameError;
    
    const emailError = validateEmail(resumeData.email);
    if (emailError) errors.email = emailError;
    
    const phoneError = validatePhone(resumeData.phone);
    if (phoneError) errors.phone = phoneError;
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Real-time validation on field change
  const updateFieldWithValidation = (field, value) => {
    setResumeData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Validate phone input - only allow numbers, +, spaces, dashes, parentheses
  const handlePhoneChange = (e) => {
    const value = e.target.value;
    // Allow only valid phone characters
    const sanitized = value.replace(/[^0-9+\s\-()]/g, '');
    updateFieldWithValidation('phone', sanitized);
  };

  // Load templates and check for pre-selected template
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await fetch(`${API}/resumes/templates`);
        if (response.ok) {
          const data = await response.json();
          setTemplates(data.templates || []);
        }
      } catch (error) {
        console.error('Failed to fetch templates:', error);
      }
    };
    fetchTemplates();
    
    // Check if template was pre-selected from homepage
    const preSelectedTemplate = sessionStorage.getItem('selectedTemplateId');
    if (preSelectedTemplate) {
      setSelectedTemplate(preSelectedTemplate);
      setStep(2);
      sessionStorage.removeItem('selectedTemplateId');
    }
    
    // Restore pending data on mount (after login redirect)
    restorePendingData();
  }, []);

  // Function to restore pending resume data from storage
  const restorePendingData = () => {
    const pendingData = sessionStorage.getItem('pendingResumeData') || localStorage.getItem('pendingResumeData');
    const pendingTemplate = sessionStorage.getItem('pendingTemplateId') || localStorage.getItem('pendingTemplateId');
    
    if (pendingData && pendingTemplate) {
      try {
        const parsedData = JSON.parse(pendingData);
        setResumeData(parsedData);
        setSelectedTemplate(pendingTemplate);
        setStep(2);
        
        // Clear from both storages after restoring
        sessionStorage.removeItem('pendingResumeData');
        sessionStorage.removeItem('pendingTemplateId');
        localStorage.removeItem('pendingResumeData');
        localStorage.removeItem('pendingTemplateId');
        
        // Show toast to inform user their data was restored
        toast.success('Your resume data has been restored!');
      } catch (e) {
        console.error('Failed to restore pending data:', e);
      }
    }
  };

  // Also restore data when user logs in (user state changes from null to user object)
  useEffect(() => {
    if (user) {
      // Check for pending payment plan after login
      const pendingPlan = sessionStorage.getItem('pendingPaymentPlan') || localStorage.getItem('pendingPaymentPlan');
      const pendingData = sessionStorage.getItem('pendingResumeData') || localStorage.getItem('pendingResumeData');
      const pendingTemplate = sessionStorage.getItem('pendingTemplateId') || localStorage.getItem('pendingTemplateId');
      
      if (pendingPlan && pendingData && pendingTemplate) {
        console.log('User logged in with pending payment plan:', pendingPlan);
        
        // Restore data first
        try {
          const parsedData = JSON.parse(pendingData);
          setResumeData(parsedData);
          setSelectedTemplate(pendingTemplate);
          setStep(2);
          
          // Clear pending payment plan (but keep resume data for download)
          sessionStorage.removeItem('pendingPaymentPlan');
          localStorage.removeItem('pendingPaymentPlan');
          
          // Check premium status
          checkPremiumStatus();
          
          // Trigger payment after a short delay
          setTimeout(() => {
            handlePayment(pendingPlan);
          }, 800);
        } catch (e) {
          console.error('Failed to restore pending data:', e);
        }
      } else if (pendingData) {
        // Just restore data without payment
        restorePendingData();
        checkPremiumStatus();
      } else {
        // Check premium status after login
        checkPremiumStatus();
      }
    }
  }, [user]);

  // Check premium status
  const checkPremiumStatus = async () => {
    if (!user) return;
    try {
      const token = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
      const response = await fetch(`${API}/users/premium-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setIsPremiumUser(data.is_premium === true);
      }
    } catch (error) {
      console.error('Failed to check premium status');
    }
  };

  // Generate live preview - debounced
  const generatePreview = useCallback(async () => {
    if (!selectedTemplate) return;
    
    try {
      const previewData = {
        full_name: resumeData.full_name || 'Your Name',
        professional_title: resumeData.professional_title || 'Your Title',
        email: resumeData.email || 'email@example.com',
        phone: resumeData.phone || '+91 98765 43210',
        location: resumeData.location || 'Your Location',
        linkedin: resumeData.linkedin || '',
        summary: resumeData.summary || 'Your professional summary will appear here...',
        skills: resumeData.skills_list.length > 0,
        skills_list: resumeData.skills_list.length > 0 ? resumeData.skills_list : ['Skill 1', 'Skill 2', 'Skill 3'],
        experience: resumeData.experience_list.length > 0,
        experience_list: resumeData.experience_list.length > 0 ? resumeData.experience_list : [{
          job_title: 'Job Title',
          company: 'Company Name',
          dates: '2020 - Present',
          achievements: ['Key achievement or responsibility']
        }],
        education: resumeData.education_list.length > 0,
        education_list: resumeData.education_list.length > 0 ? resumeData.education_list : [{
          degree: 'Your Degree',
          university: 'University Name',
          year: '2020'
        }],
        certifications: false,
        cert_list: []
      };

      const response = await fetch(`${API}/resumes/templates/${selectedTemplate}/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(previewData)
      });
      
      if (response.ok) {
        const html = await response.text();
        setPreviewHtml(html);
      }
    } catch (error) {
      console.error('Preview generation error:', error);
    }
  }, [selectedTemplate, resumeData]);

  // Update preview when data changes (debounced)
  useEffect(() => {
    if (step === 2 && selectedTemplate) {
      const timer = setTimeout(() => {
        generatePreview();
      }, 500); // Debounce 500ms
      return () => clearTimeout(timer);
    }
  }, [step, selectedTemplate, resumeData, generatePreview]);

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplate(templateId);
  };

  const handleNextStep = () => {
    if (step === 1 && !selectedTemplate) {
      toast.error('Please select a template first');
      return;
    }
    setStep(2);
  };

  // Form handlers
  const updateField = (field, value) => {
    setResumeData(prev => ({ ...prev, [field]: value }));
  };

  const addSkill = () => {
    if (newSkill.trim()) {
      setResumeData(prev => ({
        ...prev,
        skills_list: [...prev.skills_list, newSkill.trim()]
      }));
      setNewSkill('');
    }
  };

  const removeSkill = (index) => {
    setResumeData(prev => ({
      ...prev,
      skills_list: prev.skills_list.filter((_, i) => i !== index)
    }));
  };

  const addExperience = () => {
    setResumeData(prev => ({
      ...prev,
      experience_list: [...prev.experience_list, {
        job_title: '',
        company: '',
        dates: '',
        achievements: ['']
      }]
    }));
  };

  const updateExperience = (index, field, value) => {
    setResumeData(prev => ({
      ...prev,
      experience_list: prev.experience_list.map((exp, i) => 
        i === index ? { ...exp, [field]: value } : exp
      )
    }));
  };

  const removeExperience = (index) => {
    setResumeData(prev => ({
      ...prev,
      experience_list: prev.experience_list.filter((_, i) => i !== index)
    }));
  };

  const addEducation = () => {
    setResumeData(prev => ({
      ...prev,
      education_list: [...prev.education_list, {
        degree: '',
        university: '',
        year: ''
      }]
    }));
  };

  const updateEducation = (index, field, value) => {
    setResumeData(prev => ({
      ...prev,
      education_list: prev.education_list.map((edu, i) => 
        i === index ? { ...edu, [field]: value } : edu
      )
    }));
  };

  const removeEducation = (index) => {
    setResumeData(prev => ({
      ...prev,
      education_list: prev.education_list.filter((_, i) => i !== index)
    }));
  };

  // Handle Save - requires login
  const handleSave = async () => {
    if (!resumeData.full_name) {
      toast.error('Please enter at least your name');
      return;
    }
    
    if (!user) {
      // Store data in BOTH sessionStorage and localStorage for reliability
      const resumeDataStr = JSON.stringify(resumeData);
      sessionStorage.setItem('pendingResumeData', resumeDataStr);
      sessionStorage.setItem('pendingTemplateId', selectedTemplate);
      sessionStorage.setItem('redirectAfterLogin', '/create-resume');
      // Also store in localStorage as backup (survives redirects better)
      localStorage.setItem('pendingResumeData', resumeDataStr);
      localStorage.setItem('pendingTemplateId', selectedTemplate);
      localStorage.setItem('redirectAfterLogin', '/create-resume');
      setShowLoginModal(true);
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
      const response = await fetch(`${API}/resumes/save-created`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          template_id: selectedTemplate,
          resume_data: resumeData
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSavedResumeId(data.resume_id);
        toast.success('Resume saved successfully!');
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save resume. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Handle Download - different logic for free vs premium templates
  const handleDownload = async () => {
    // Validate required fields
    if (!validateForm()) {
      toast.error('Please fill in all required fields correctly');
      return;
    }
    
    const templateData = templates.find(t => t.id === selectedTemplate);
    
    // FREE TEMPLATE: No login required, download with watermark
    if (!templateData?.isPremium) {
      // Free template - anyone can download with watermark
      await downloadResume(true); // Always with watermark for free
      return;
    }
    
    // PREMIUM TEMPLATE: Login required
    if (!user) {
      sessionStorage.setItem('pendingResumeData', JSON.stringify(resumeData));
      sessionStorage.setItem('pendingTemplateId', selectedTemplate);
      localStorage.setItem('pendingResumeData', JSON.stringify(resumeData));
      localStorage.setItem('pendingTemplateId', selectedTemplate);
      localStorage.setItem('redirectAfterLogin', '/create-resume');
      sessionStorage.setItem('redirectAfterLogin', '/create-resume');
      setShowLoginModal(true);
      return;
    }
    
    // Premium template + logged in user
    if (!isPremiumUser) {
      // Show paywall for premium templates
      setShowPaywallModal(true);
      return;
    }
    
    // Premium user - download without watermark
    await downloadResume(false);
  };

  const downloadResume = async (withWatermark = true) => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
      
      // Build headers - token optional for free downloads
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API}/resumes/download`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          template_id: selectedTemplate,
          resume_data: resumeData,
          with_watermark: withWatermark
        })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${resumeData.full_name?.replace(/\s+/g, '_') || 'resume'}_FixResume24.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast.success('Resume downloaded successfully!');
        setShowPaywallModal(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Download failed. Please try again.');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Download failed. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handlePayment = async (plan) => {
    setPaymentProcessing(true);
    
    try {
      const token = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
      
      // STEP 1: First save the resume to get a download_id (backend-driven flow)
      const saveResponse = await fetch(`${API}/resumes/create-and-save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          template_id: selectedTemplate,
          resume_data: resumeData
        })
      });
      
      if (!saveResponse.ok) {
        const saveError = await saveResponse.json();
        throw new Error(saveError.detail || 'Failed to save resume');
      }
      
      const saveData = await saveResponse.json();
      const downloadId = saveData.download_id;
      
      // Store for after payment
      sessionStorage.setItem('paymentPlan', plan);
      sessionStorage.setItem('downloadId', downloadId);
      sessionStorage.setItem('createResumeTemplate', selectedTemplate);
      
      // STEP 2: Create payment order with download_id
      const response = await fetch(`${API}/users/create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          plan: plan,
          download_id: downloadId
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create order');
      }

      const orderData = await response.json();
      
      const options = {
        key: orderData.razorpay_key_id || orderData.key_id,
        amount: orderData.amount * 100, // Convert to paise
        currency: orderData.currency,
        name: 'FixResume24',
        description: plan === 'single' ? 'Single Clean Download (₹29)' : 'Premium Access 24hrs (₹79)',
        order_id: orderData.order_id,
        handler: function (razorpayResponse) {
          // Use synchronous handler, then call async verification
          verifyPaymentAndRedirectCreate(
            razorpayResponse.razorpay_order_id,
            razorpayResponse.razorpay_payment_id,
            razorpayResponse.razorpay_signature
          );
        },
        prefill: {
          name: user?.name || '',
          email: user?.email || ''
        },
        theme: {
          color: '#1e3a5f'
        },
        modal: {
          ondismiss: function() {
            setPaymentProcessing(false);
            toast.info('Payment cancelled. You can try again.');
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
      
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.message || 'Failed to initiate payment. Please try again.');
      setPaymentProcessing(false);
    }
  };

  // Separate async function for payment verification - REDIRECTS to download page
  const verifyPaymentAndRedirectCreate = async (orderId, paymentId, signature) => {
    try {
      const token = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
      const plan = sessionStorage.getItem('paymentPlan');
      const downloadId = sessionStorage.getItem('downloadId');
      
      console.log('Verifying payment for CreateResume:', { orderId, paymentId, plan, downloadId });
      
      const verifyResponse = await fetch(`${API}/users/verify-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          razorpay_order_id: orderId,
          razorpay_payment_id: paymentId,
          razorpay_signature: signature,
          plan: plan,
          download_id: downloadId
        })
      });

      if (verifyResponse.ok) {
        const result = await verifyResponse.json();
        toast.success('Payment successful!');
        
        // Only set premium for ₹79 plan
        if (plan === 'premium') {
          setIsPremiumUser(true);
        }
        
        setShowPaywallModal(false);
        
        // Clean up session storage
        sessionStorage.removeItem('paymentPlan');
        sessionStorage.removeItem('downloadId');
        sessionStorage.removeItem('createResumeTemplate');
        
        // REDIRECT to download page with download_id (NOT auto-download)
        const finalDownloadId = result.download_id || downloadId;
        if (finalDownloadId) {
          window.location.href = `/download?download_id=${finalDownloadId}`;
        } else {
          toast.error('Download link not available. Please contact support.');
        }
      } else {
        const errorData = await verifyResponse.json();
        console.error('Payment verification failed:', errorData);
        toast.error('Payment verification failed. Please contact support.');
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      toast.error('Payment verification failed. Please contact support.');
    }
    setPaymentProcessing(false);
  };

  // Handle login with plan - stores state and redirects to login
  const handleLoginWithPlan = (plan) => {
    // Store all state for after login
    sessionStorage.setItem('pendingResumeData', JSON.stringify(resumeData));
    sessionStorage.setItem('pendingTemplateId', selectedTemplate);
    sessionStorage.setItem('pendingPaymentPlan', plan);
    sessionStorage.setItem('redirectAfterLogin', '/create-resume');
    localStorage.setItem('pendingResumeData', JSON.stringify(resumeData));
    localStorage.setItem('pendingTemplateId', selectedTemplate);
    localStorage.setItem('pendingPaymentPlan', plan);
    localStorage.setItem('redirectAfterLogin', '/create-resume');
    
    setShowPaywallModal(false);
    navigate('/login');
  };

  const handleLoginRedirect = () => {
    // Store state before redirecting to login
    sessionStorage.setItem('pendingResumeData', JSON.stringify(resumeData));
    sessionStorage.setItem('pendingTemplateId', selectedTemplate);
    sessionStorage.setItem('redirectAfterLogin', '/create-resume');
    localStorage.setItem('pendingResumeData', JSON.stringify(resumeData));
    localStorage.setItem('pendingTemplateId', selectedTemplate);
    localStorage.setItem('redirectAfterLogin', '/create-resume');
    
    setShowLoginModal(false);
    navigate('/login');
  };

  const selectedTemplateData = templates.find(t => t.id === selectedTemplate);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b border-border bg-white/80 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            {/* Left: Back Button + Logo */}
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-1 text-muted-foreground hover:text-primary transition-colors" data-testid="back-home-btn">
                <ArrowLeft className="h-4 w-4" />
                <span className="hidden sm:inline text-sm">Home</span>
              </Link>
              <Link to="/" className="flex items-center space-x-2" data-testid="logo-link">
                <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                  <FileText className="h-5 w-5 text-white" />
                </div>
                <span className="font-heading font-bold text-xl text-primary hidden sm:block">
                  FixResume24
                </span>
              </Link>
            </div>

            {/* Step Indicator */}
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= 1 ? 'bg-primary text-white' : 'bg-slate-200 text-slate-500'
              }`}>
                1
              </div>
              <div className={`w-8 h-0.5 ${step > 1 ? 'bg-primary' : 'bg-slate-200'}`} />
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= 2 ? 'bg-primary text-white' : 'bg-slate-200 text-slate-500'
              }`}>
                2
              </div>
            </div>

            {user ? (
              <span className="text-sm text-muted-foreground">Hi, {user.name?.split(' ')[0]}</span>
            ) : (
              <Link to="/login">
                <Button variant="outline" size="sm">Sign In</Button>
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Step 1: Select Template */}
        {step === 1 && (
          <div className="pb-20">
            <div className="text-center mb-4">
              <h1 className="text-3xl font-bold text-slate-900 mb-2">Choose Your Template</h1>
              <p className="text-muted-foreground">Select a template that best fits your style</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
              {templates.map((template) => (
                <TemplateSelectCard
                  key={template.id}
                  template={template}
                  isSelected={selectedTemplate === template.id}
                  onSelect={() => handleTemplateSelect(template.id)}
                />
              ))}
            </div>

            {/* Sticky floating button - visible on both mobile and desktop */}
            <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t shadow-lg z-50">
              <div className="max-w-7xl mx-auto flex justify-end">
                <Button 
                  onClick={handleNextStep} 
                  disabled={!selectedTemplate} 
                  data-testid="next-step-btn-sticky" 
                  size="lg"
                  className="w-full sm:w-auto"
                >
                  Next: Enter Details
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Form + Live Preview */}
        {step === 2 && (
          <div className="pb-20 sm:pb-0">
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Enter Details</h1>
              <p className="text-muted-foreground">Fill in your details and see live preview</p>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              {/* Left: Form */}
              <div>
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">Your Details</CardTitle>
                      <Button variant="ghost" size="sm" onClick={() => setStep(1)}>
                        <ArrowLeft className="h-4 w-4 mr-1" />
                        Change Template
                      </Button>
                    </div>
                    {/* Section progress indicator */}
                    <div className="flex items-center gap-2 mt-3">
                      {['Personal', 'Work', 'Education', 'Skills'].map((label, idx) => (
                        <button
                          key={label}
                          onClick={() => setFormSection(idx + 1)}
                          className={`flex-1 py-2 px-1 text-xs rounded-md transition-colors ${
                            formSection === idx + 1 
                              ? 'bg-primary text-white' 
                              : formSection > idx + 1 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-slate-100 text-slate-500'
                          }`}
                        >
                          {formSection > idx + 1 && <CheckCircle className="h-3 w-3 inline mr-1" />}
                          {label}
                        </button>
                      ))}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Personal Section */}
                    {formSection === 1 && (
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label htmlFor="full_name" className="text-xs">Full Name <span className="text-red-500">*</span></Label>
                            <Input
                              id="full_name"
                              value={resumeData.full_name}
                              onChange={(e) => updateFieldWithValidation('full_name', e.target.value)}
                              placeholder="John Doe"
                              className={`h-9 ${validationErrors.full_name ? 'border-red-500 focus:ring-red-500' : ''}`}
                              data-testid="input-full-name"
                            />
                            {validationErrors.full_name && (
                              <p className="text-xs text-red-500 mt-1">{validationErrors.full_name}</p>
                            )}
                          </div>
                          <div>
                            <Label htmlFor="professional_title" className="text-xs">Title</Label>
                            <Input
                              id="professional_title"
                              value={resumeData.professional_title}
                              onChange={(e) => updateField('professional_title', e.target.value)}
                              placeholder="Software Engineer"
                              className="h-9"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label htmlFor="email" className="text-xs">Email <span className="text-red-500">*</span></Label>
                            <Input
                              id="email"
                              type="email"
                              value={resumeData.email}
                              onChange={(e) => updateFieldWithValidation('email', e.target.value)}
                              placeholder="john@example.com"
                              className={`h-9 ${validationErrors.email ? 'border-red-500 focus:ring-red-500' : ''}`}
                            />
                            {validationErrors.email && (
                              <p className="text-xs text-red-500 mt-1">{validationErrors.email}</p>
                            )}
                          </div>
                          <div>
                            <Label htmlFor="phone" className="text-xs">Phone <span className="text-red-500">*</span></Label>
                            <Input
                              id="phone"
                              value={resumeData.phone}
                              onChange={handlePhoneChange}
                              placeholder="+91 98765 43210"
                              className={`h-9 ${validationErrors.phone ? 'border-red-500 focus:ring-red-500' : ''}`}
                            />
                            {validationErrors.phone && (
                              <p className="text-xs text-red-500 mt-1">{validationErrors.phone}</p>
                            )}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label htmlFor="location" className="text-xs">Location</Label>
                            <Input
                              id="location"
                              value={resumeData.location}
                              onChange={(e) => updateField('location', e.target.value)}
                              placeholder="Mumbai, India"
                              className="h-9"
                            />
                          </div>
                          <div>
                            <Label htmlFor="linkedin" className="text-xs">LinkedIn</Label>
                            <Input
                              id="linkedin"
                              value={resumeData.linkedin}
                              onChange={(e) => updateField('linkedin', e.target.value)}
                              placeholder="linkedin.com/in/johndoe"
                              className="h-9"
                            />
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="summary" className="text-xs">Professional Summary</Label>
                          <Textarea
                            id="summary"
                            value={resumeData.summary}
                            onChange={(e) => updateField('summary', e.target.value)}
                            placeholder="Brief summary of your professional background..."
                            rows={3}
                            className="text-sm"
                          />
                        </div>
                        {/* Next Button */}
                        <div className="pt-4 flex justify-end">
                          <Button onClick={() => setFormSection(2)} data-testid="next-to-work">
                            Next: Work Experience
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Experience Section */}
                    {formSection === 2 && (
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-muted-foreground">
                            {resumeData.experience_list.length} experience(s) added
                          </span>
                          <Button variant="outline" size="sm" onClick={addExperience}>
                            <Plus className="h-3 w-3 mr-1" />
                            Add
                          </Button>
                        </div>
                        <div className="space-y-3 max-h-[300px] overflow-y-auto">
                          {resumeData.experience_list.map((exp, index) => (
                            <div key={index} className="border rounded-lg p-3 space-y-2">
                              <div className="flex justify-between items-start">
                                <span className="text-xs font-medium text-muted-foreground">#{index + 1}</span>
                                <Button variant="ghost" size="sm" onClick={() => removeExperience(index)} className="h-6 w-6 p-0 text-destructive">
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <Input
                                  placeholder="Job Title"
                                  value={exp.job_title}
                                  onChange={(e) => updateExperience(index, 'job_title', e.target.value)}
                                  className="h-8 text-sm"
                                />
                                <Input
                                  placeholder="Company"
                                  value={exp.company}
                                  onChange={(e) => updateExperience(index, 'company', e.target.value)}
                                  className="h-8 text-sm"
                                />
                              </div>
                              <Input
                                placeholder="Dates (e.g., 2020 - Present)"
                                value={exp.dates}
                                onChange={(e) => updateExperience(index, 'dates', e.target.value)}
                                className="h-8 text-sm"
                              />
                              <Textarea
                                placeholder="Key achievements (one per line)"
                                value={exp.achievements?.join('\n') || ''}
                                onChange={(e) => updateExperience(index, 'achievements', e.target.value.split('\n'))}
                                rows={2}
                                className="text-sm"
                              />
                            </div>
                          ))}
                          {resumeData.experience_list.length === 0 && (
                            <p className="text-center text-muted-foreground py-6 text-sm">
                              No experience added. Click "Add" to start.
                            </p>
                          )}
                        </div>
                        {/* Navigation Buttons */}
                        <div className="pt-4 flex justify-between">
                          <Button variant="outline" onClick={() => setFormSection(1)}>
                            <ArrowLeft className="mr-2 h-4 w-4" />
                            Back
                          </Button>
                          <Button onClick={() => setFormSection(3)} data-testid="next-to-education">
                            Next: Education
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Education Section */}
                    {formSection === 3 && (
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-muted-foreground">
                            {resumeData.education_list.length} education(s) added
                          </span>
                          <Button variant="outline" size="sm" onClick={addEducation}>
                            <Plus className="h-3 w-3 mr-1" />
                            Add
                          </Button>
                        </div>
                        <div className="space-y-3 max-h-[300px] overflow-y-auto">
                          {resumeData.education_list.map((edu, index) => (
                            <div key={index} className="border rounded-lg p-3 space-y-2">
                              <div className="flex justify-between items-start">
                                <span className="text-xs font-medium text-muted-foreground">#{index + 1}</span>
                                <Button variant="ghost" size="sm" onClick={() => removeEducation(index)} className="h-6 w-6 p-0 text-destructive">
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                              <Input
                                placeholder="Degree (e.g., B.Tech in CS)"
                                value={edu.degree}
                                onChange={(e) => updateEducation(index, 'degree', e.target.value)}
                                className="h-8 text-sm"
                              />
                              <div className="grid grid-cols-2 gap-2">
                                <Input
                                  placeholder="University"
                                  value={edu.university}
                                  onChange={(e) => updateEducation(index, 'university', e.target.value)}
                                  className="h-8 text-sm"
                                />
                                <Input
                                  placeholder="Year"
                                  value={edu.year}
                                  onChange={(e) => updateEducation(index, 'year', e.target.value)}
                                  className="h-8 text-sm"
                                />
                              </div>
                            </div>
                          ))}
                          {resumeData.education_list.length === 0 && (
                            <p className="text-center text-muted-foreground py-6 text-sm">
                              No education added. Click "Add" to start.
                            </p>
                          )}
                        </div>
                        {/* Navigation Buttons */}
                        <div className="pt-4 flex justify-between">
                          <Button variant="outline" onClick={() => setFormSection(2)}>
                            <ArrowLeft className="mr-2 h-4 w-4" />
                            Back
                          </Button>
                          <Button onClick={() => setFormSection(4)} data-testid="next-to-skills">
                            Next: Skills
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Skills Section */}
                    {formSection === 4 && (
                      <div className="space-y-3">
                        <div className="flex gap-2">
                          <Input
                            placeholder="Add a skill (e.g., Python, React)"
                            value={newSkill}
                            onChange={(e) => setNewSkill(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && addSkill()}
                            className="h-9"
                          />
                          <Button onClick={addSkill} size="sm">
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="flex flex-wrap gap-2 min-h-[100px]">
                          {resumeData.skills_list.map((skill, index) => (
                            <Badge key={index} variant="secondary" className="text-sm py-1 px-2">
                              {skill}
                              <button onClick={() => removeSkill(index)} className="ml-1 hover:text-destructive">×</button>
                            </Badge>
                          ))}
                          {resumeData.skills_list.length === 0 && (
                            <p className="text-muted-foreground text-sm">No skills added yet</p>
                          )}
                        </div>
                        {/* Navigation Buttons */}
                        <div className="pt-4 flex justify-between">
                          <Button variant="outline" onClick={() => setFormSection(3)}>
                            <ArrowLeft className="mr-2 h-4 w-4" />
                            Back
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Action Buttons - Always visible on desktop, sticky on mobile when on Skills section */}
                <Card className={`mt-4 ${formSection === 4 ? '' : 'hidden sm:block'}`}>
                  <CardContent className="p-4">
                    <div className="flex gap-3">
                      <Button 
                        variant="outline" 
                        className="flex-1"
                        onClick={handleSave}
                        disabled={saving || !resumeData.full_name}
                        data-testid="save-resume-btn"
                      >
                        {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                        {user ? 'Save' : 'Sign In to Save'}
                      </Button>
                      <Button 
                        className="flex-1"
                        onClick={handleDownload}
                        disabled={downloading || !resumeData.full_name}
                        data-testid="download-btn"
                      >
                        {downloading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
                        Download
                      </Button>
                    </div>
                    {selectedTemplateData && (
                      <div className="mt-3 flex items-center justify-center gap-2">
                        {selectedTemplateData.isPremium ? (
                          <Badge className="bg-amber-100 text-amber-700">
                            <Crown className="h-3 w-3 mr-1" />
                            Premium Template
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-green-700 border-green-300">
                            Free Template
                          </Badge>
                        )}
                        {selectedTemplateData.ats_label && (
                          <Badge variant="outline">
                            <Shield className="h-3 w-3 mr-1" />
                            {selectedTemplateData.ats_label}
                          </Badge>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Sticky Download button on mobile for Skills section */}
                {formSection === 4 && (
                  <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t shadow-lg sm:hidden z-50">
                    <div className="flex gap-3">
                      <Button 
                        variant="outline" 
                        className="flex-1"
                        onClick={handleSave}
                        disabled={saving || !resumeData.full_name}
                      >
                        {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                        Save
                      </Button>
                      <Button 
                        className="flex-1"
                        onClick={handleDownload}
                        disabled={downloading || !resumeData.full_name}
                      >
                        {downloading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
                        Download
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Right: Live Preview */}
              <div className="hidden lg:block">
                <Card className="sticky top-24">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Eye className="h-5 w-5" />
                      Live Preview
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="bg-white border-t" style={{ height: '600px', overflow: 'hidden' }}>
                      {previewHtml ? (
                        <iframe
                          srcDoc={previewHtml}
                          title="Resume Preview"
                          className="w-full h-full border-0"
                          sandbox="allow-same-origin"
                        />
                      ) : (
                        <div className="h-full flex items-center justify-center bg-slate-50">
                          <div className="text-center text-muted-foreground">
                            <FileText className="h-12 w-12 mx-auto mb-2 opacity-30" />
                            <p>Start filling your details to see preview</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Login Modal */}
      <Dialog open={showLoginModal} onOpenChange={setShowLoginModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center">Sign In Required</DialogTitle>
            <DialogDescription className="text-center">
              Please sign in to save and download your resume
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 py-4">
            <Button onClick={handleLoginRedirect} className="w-full">
              <LogIn className="mr-2 h-4 w-4" />
              Sign In with Google
            </Button>
            <Button variant="outline" onClick={() => setShowLoginModal(false)}>
              Continue Editing
            </Button>
          </div>
          <p className="text-xs text-center text-muted-foreground">
            Your progress is saved. You can continue after signing in.
          </p>
        </DialogContent>
      </Dialog>

      {/* Paywall Modal - Only for PREMIUM templates */}
      <Dialog open={showPaywallModal} onOpenChange={setShowPaywallModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-center">
              <Crown className="h-8 w-8 text-amber-500 mx-auto mb-2" />
              <span className="text-xl">Download Options</span>
            </DialogTitle>
            <DialogDescription className="text-center">
              Premium template selected - Choose your plan
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            {/* ₹29 Option - Clean download for current template */}
            <Card 
              className="cursor-pointer hover:border-primary transition-colors"
              data-testid="payment-option-29"
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="font-semibold">Clean Download</p>
                    <p className="text-sm text-muted-foreground">No watermark, current template only</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-primary">₹29</p>
                    <p className="text-xs text-muted-foreground">one-time</p>
                  </div>
                </div>
                {user ? (
                  <Button 
                    className="w-full" 
                    onClick={() => !paymentProcessing && handlePayment('single')}
                    disabled={paymentProcessing}
                  >
                    {paymentProcessing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Pay ₹29
                  </Button>
                ) : (
                  <Button 
                    className="w-full" 
                    variant="outline"
                    onClick={() => handleLoginWithPlan('single')}
                  >
                    <LogIn className="h-4 w-4 mr-2" />
                    Sign In to Pay ₹29
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* ₹79 Option - Full premium access */}
            <Card 
              className="cursor-pointer border-primary bg-primary/5 hover:bg-primary/10 transition-colors relative"
              data-testid="payment-option-79"
            >
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <Badge className="bg-accent text-white">Best Value</Badge>
              </div>
              <CardContent className="p-4 pt-6">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="font-semibold flex items-center gap-2">
                      <Crown className="h-4 w-4 text-amber-500" />
                      Premium Access
                    </p>
                    <ul className="text-sm text-muted-foreground mt-1 space-y-0.5">
                      <li className="flex items-center gap-1">
                        <CheckCircle className="h-3 w-3 text-green-500" />
                        All 29 templates
                      </li>
                      <li className="flex items-center gap-1">
                        <CheckCircle className="h-3 w-3 text-green-500" />
                        Unlimited downloads
                      </li>
                      <li className="flex items-center gap-1">
                        <CheckCircle className="h-3 w-3 text-green-500" />
                        24-hour access
                      </li>
                    </ul>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-primary">₹79</p>
                    <p className="text-xs text-muted-foreground">24 hours</p>
                  </div>
                </div>
                {user ? (
                  <Button 
                    className="w-full bg-primary" 
                    onClick={() => !paymentProcessing && handlePayment('premium')}
                    disabled={paymentProcessing}
                  >
                    {paymentProcessing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Pay ₹79
                  </Button>
                ) : (
                  <Button 
                    className="w-full"
                    onClick={() => handleLoginWithPlan('premium')}
                  >
                    <LogIn className="h-4 w-4 mr-2" />
                    Sign In to Pay ₹79
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>

          <p className="text-xs text-center text-muted-foreground">
            Secure payment via Razorpay. No auto-renewal.
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Template Select Card Component
const TemplateSelectCard = ({ template, isSelected, onSelect }) => {
  const [previewHtml, setPreviewHtml] = useState(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  useEffect(() => {
    const fetchPreview = async () => {
      try {
        const response = await fetch(`${API}/resumes/templates/${template.id}/preview`);
        if (response.ok) {
          const html = await response.text();
          setPreviewHtml(html);
        }
      } catch (error) {
        console.log('Preview fetch error');
      }
    };
    fetchPreview();
  }, [template.id, API]);

  return (
    <Card 
      className={`cursor-pointer transition-all overflow-hidden ${
        isSelected ? 'ring-2 ring-primary border-primary' : 'hover:shadow-md border-slate-200'
      }`}
      onClick={onSelect}
      data-testid={`template-select-${template.id}`}
    >
      <CardContent className="p-0">
        <div className="h-40 overflow-hidden bg-white border-b relative">
          {previewHtml ? (
            <div 
              className="transform scale-[0.25] origin-top-left w-[400%] h-[400%] pointer-events-none"
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
          ) : (
            <div className="h-full flex items-center justify-center bg-slate-50">
              <FileText className="h-8 w-8 text-slate-300" />
            </div>
          )}
          {isSelected && (
            <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-primary" />
            </div>
          )}
        </div>
        <div className="p-3">
          <p className="font-medium text-sm truncate">{template.name}</p>
          <div className="flex gap-1 mt-1">
            {template.isPremium ? (
              <Badge className="bg-amber-100 text-amber-700 text-xs py-0">
                <Crown className="h-2.5 w-2.5 mr-0.5" />
                Pro
              </Badge>
            ) : (
              <Badge variant="outline" className="text-xs py-0 text-green-700 border-green-300">
                Free
              </Badge>
            )}
            {template.ats_label && (
              <Badge variant="outline" className="text-xs py-0">
                <Shield className="h-2.5 w-2.5 mr-0.5" />
                ATS
              </Badge>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CreateResumePage;
